import { Switch, Route, Redirect, Link } from "wouter";
import { lazy, Suspense, useEffect, useState } from "react";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  useAuth,
  AUTH_REDIRECT_COUNT_KEY,
  AUTH_REDIRECT_FIRST_AT_KEY,
  clearAuthRedirectCounter,
} from "@/hooks/use-auth";
import { Loader2 } from "lucide-react";
import { RecordingProvider, useRecordingStatus } from "@/contexts/recording-context";

const AUTH_REDIRECT_MAX_ATTEMPTS = 2;
const AUTH_REDIRECT_WINDOW_MS = 10_000;

function buildLoginUrl() {
  const path = window.location.pathname + window.location.search;
  if (!path || path === "/" || path.startsWith("//") || !path.startsWith("/")) {
    return "/api/login";
  }
  return `/api/login?returnTo=${encodeURIComponent(path)}`;
}

const LandingPage = lazy(() => import("@/pages/LandingPage"));
const FeaturesPage = lazy(() => import("@/pages/FeaturesPage"));
import Dashboard from "@/pages/Dashboard";
import Templates from "@/pages/Templates";
import GlobalRules from "@/pages/GlobalRules";
import Profiles from "@/pages/Profiles";
import ChatDashboard from "@/pages/ChatDashboard";
import Settings from "@/pages/Settings";
import Team from "@/pages/Team";
import JoinTeam from "@/pages/JoinTeam";
import ChangeLog from "@/pages/ChangeLog";
import Security from "@/pages/Security";
import NotFound from "@/pages/NotFound";
import { PrivacyPage, TermsPage, ContactPage } from "@/pages/LegalPages";
import AdminArea from "@/pages/admin/AdminArea";

function ProtectedRoute({ component: Component }: { component: React.ComponentType }) {
  const { isAuthenticated, isLoading } = useAuth();
  const { isRecordingActive } = useRecordingStatus();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated && !isRecordingActive) {
    return <LoginRedirect />;
  }

  return <Component />;
}

function AuthLoopError({ onRetry }: { onRetry: () => void }) {
  return (
    <div
      data-testid="auth-loop-error"
      className="min-h-screen flex items-center justify-center bg-background p-6"
    >
      <div className="max-w-md w-full bg-card border border-border rounded-2xl p-8 shadow-lg text-center">
        <h1 className="text-2xl font-bold text-foreground mb-3">
          We couldn't sign you in
        </h1>
        <p className="text-muted-foreground mb-6">
          We tried a few times but couldn't complete sign-in. This usually
          means your browser blocked the session cookie, or sign-in was
          interrupted. You can try again or head back to the home page.
        </p>
        <div className="flex flex-col gap-3">
          <button
            data-testid="button-auth-loop-retry"
            onClick={onRetry}
            className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
          >
            Try again
          </button>
          <Link
            href="/"
            data-testid="link-auth-loop-home"
            className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
          >
            Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}

function LoginRedirect() {
  const [looped, setLooped] = useState(false);

  useEffect(() => {
    let count = 0;
    let firstAt = 0;
    try {
      count = Number(sessionStorage.getItem(AUTH_REDIRECT_COUNT_KEY) || "0") || 0;
      firstAt = Number(sessionStorage.getItem(AUTH_REDIRECT_FIRST_AT_KEY) || "0") || 0;
    } catch {
      // ignore
    }

    const now = Date.now();
    if (!firstAt || now - firstAt > AUTH_REDIRECT_WINDOW_MS) {
      count = 0;
      firstAt = now;
    }

    if (count >= AUTH_REDIRECT_MAX_ATTEMPTS) {
      setLooped(true);
      return;
    }

    try {
      sessionStorage.setItem(AUTH_REDIRECT_COUNT_KEY, String(count + 1));
      sessionStorage.setItem(AUTH_REDIRECT_FIRST_AT_KEY, String(firstAt));
    } catch {
      // ignore
    }
    window.location.href = buildLoginUrl();
  }, []);

  if (looped) {
    return (
      <AuthLoopError
        onRetry={() => {
          clearAuthRedirectCounter();
          window.location.href = buildLoginUrl();
        }}
      />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

function Router() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Switch>
      <Route path="/">
        {isAuthenticated ? (
          <Redirect to="/dashboard" />
        ) : (
          <Suspense
            fallback={
              <div className="min-h-screen flex items-center justify-center bg-background">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            }
          >
            <LandingPage />
          </Suspense>
        )}
      </Route>
      
      <Route path="/dashboard">
        <ProtectedRoute component={Dashboard} />
      </Route>

      <Route path="/dashboard/session/:sessionId">
        <ProtectedRoute component={Dashboard} />
      </Route>

      <Route path="/dashboard/session/:sessionId/:tab">
        <ProtectedRoute component={Dashboard} />
      </Route>

      <Route path="/templates">
        <ProtectedRoute component={Templates} />
      </Route>

      <Route path="/templates/:id">
        <ProtectedRoute component={Templates} />
      </Route>

      <Route path="/rules">
        <ProtectedRoute component={GlobalRules} />
      </Route>

      <Route path="/rules/:id">
        <ProtectedRoute component={GlobalRules} />
      </Route>

      <Route path="/profiles">
        <ProtectedRoute component={Profiles} />
      </Route>

      <Route path="/chat">
        <ProtectedRoute component={ChatDashboard} />
      </Route>

      <Route path="/chat/:chatId">
        <ProtectedRoute component={ChatDashboard} />
      </Route>

      <Route path="/settings">
        <ProtectedRoute component={Settings} />
      </Route>

      <Route path="/team">
        <ProtectedRoute component={Team} />
      </Route>

      <Route path="/join">
        <ProtectedRoute component={JoinTeam} />
      </Route>

      <Route path="/join/:token">
        <ProtectedRoute component={JoinTeam} />
      </Route>

      <Route path="/change-log">
        <ProtectedRoute component={ChangeLog} />
      </Route>

      <Route path="/security">
        <ProtectedRoute component={Security} />
      </Route>

      <Route path="/admin/settings">
        <ProtectedRoute component={() => <AdminArea section="settings" />} />
      </Route>

      <Route path="/admin/site-admins">
        <ProtectedRoute component={() => <AdminArea section="site-admins" />} />
      </Route>

      <Route path="/admin/usage">
        <ProtectedRoute component={() => <AdminArea section="usage" />} />
      </Route>

      <Route path="/admin/models">
        <ProtectedRoute component={() => <AdminArea section="models" />} />
      </Route>

      <Route path="/admin/identities">
        <ProtectedRoute component={() => <AdminArea section="identities" />} />
      </Route>

      <Route path="/admin">
        <ProtectedRoute component={() => <AdminArea section={null} />} />
      </Route>

      <Route path="/features">
        <Suspense
          fallback={
            <div className="min-h-screen flex items-center justify-center bg-background">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          }
        >
          <FeaturesPage />
        </Suspense>
      </Route>

      <Route path="/privacy" component={PrivacyPage} />
      <Route path="/terms" component={TermsPage} />
      <Route path="/contact" component={ContactPage} />

      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RecordingProvider>
        <TooltipProvider>
          <Toaster />
          <Router />
        </TooltipProvider>
      </RecordingProvider>
    </QueryClientProvider>
  );
}

export default App;
